// The process page's one duration histogram: cases in the Cases tab, step occurrences in the Steps
// tab and a step's own detail. It scrolls sideways and zooms with ⌘/Ctrl + scroll around the
// pointer, and gains resolution as it zooms, since bars are sized to the drawn width rather than
// fixed in number. Dragging selects a filter range when onSelectionChange is supplied; otherwise
// it zooms horizontally into the dragged range. Clicking a bar uses that bar's range.
import { MessageCircleQuestion, ZoomOut } from "lucide-react";
// biome-ignore lint/style/useImportType: this bundle compiles JSX with the classic React runtime.
import React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { useResponsiveWidth } from "../../utils/useResponsiveWidth";
import { NICE_STEPS, clamp, formatAxisDuration } from "./chartAxis";
import { durationHistogramBins } from "./processDurationStats";
import { ChartTooltip, HelpHint } from "./processSidebarUi";

const PLOT_TOP = 16;
const PLOT_BOTTOM = 26;
const AXIS_W = 36;
// Keeps the first and last bars (and the range edges drawn on them) clear of the scroller's edge.
const PAD_X = 8;
// About one bar per this many pixels of drawn width, within these bounds.
const BIN_TARGET_PX = 22;
const MIN_BINS = 8;
const MAX_BINS = 400;
const MAX_ZOOM = 16;
const MIN_TICK_GAP_PX = 72;
// A press that travels less than this is a click rather than a range drag.
const MIN_DRAG_PX = 4;
const MARK_OPACITY = 0.7;
const MARK_OPACITY_HOVER = 0.92;
export type DurationRange = { min: number; max: number };

type Drag = { anchor: number; current: number; moved: boolean; bin: number | null };

const DurationHistogram = ({
  values,
  height = 150,
  countLabel,
  title,
  description,
  outlierAbove,
  selection,
  showSelectionSummary = true,
  onSelectionChange,
  onAskVariability,
}: {
  values: number[];
  /** Plot and axis height; compact panels can reserve more room for case rows. */
  height?: number;
  /** What one observation is, plural: "cases", "occurrences". */
  countLabel: string;
  /** Shown above the chart; false leaves the line out when a heading already names it. */
  title?: React.ReactNode;
  /** Explains the reference statistics in the chart's help tooltip. */
  description?: string;
  /** Bars wholly past this duration are painted as outliers, past a dashed line. */
  outlierAbove?: number | null;
  /** The selected span, in seconds; an open-ended one has `max: Infinity`. */
  selection?: DurationRange | null;
  /** A parent may already describe the selection and provide its clear action. */
  showSelectionSummary?: boolean;
  /** Makes the chart filterable by clicking a bar or dragging across it. */
  onSelectionChange?: (range: DurationRange | null) => void;
  onAskVariability?: () => void;
}) => {
  const { t } = useTranslation();
  const plotHeight = height - PLOT_TOP - PLOT_BOTTOM;
  const [containerRef, containerW] = useResponsiveWidth(360);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [zoomRange, setZoomRange] = useState<DurationRange | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: A new dataset resets the inspected range.
  useEffect(() => setZoomRange(null), [values]);
  const [hover, setHover] = useState<number | null>(null);
  // The drag lives in a ref that the pointer handlers read, and is mirrored into state only to
  // draw it: pointer events can arrive faster than renders, and a handler reading the render's
  // copy would see a drag that already moved on (or not yet started).
  const dragRef = useRef<Drag | null>(null);
  const pointerCommitted = useRef(false);
  const [drag, setDragState] = useState<Drag | null>(null);
  const setDrag = (next: Drag | null) => {
    dragRef.current = next;
    setDragState(next);
  };
  // Scroll position only matters to a visible tooltip; the tick re-renders just for that.
  const [scrollLeft, setScrollLeft] = useState(0);
  const pendingScroll = useRef<{ fraction: number; offsetX: number } | null>(null);

  const viewportW = Math.max(120, containerW - AXIS_W);
  const plotW = (viewportW - PAD_X * 2) * zoom;
  const binCount = clamp(Math.round(plotW / BIN_TARGET_PX), MIN_BINS, MAX_BINS);
  const viewRange = selection ?? zoomRange;
  const domainMin = viewRange?.min ?? 0;
  const domainMax = Math.max(
    domainMin + 1,
    viewRange && Number.isFinite(viewRange.max) ? viewRange.max : Math.ceil(Math.max(0, ...values)),
  );
  const domainSpan = domainMax - domainMin;
  const bins = useMemo(
    () =>
      durationHistogramBins(values, binCount, {
        min: domainMin,
        max: domainMax,
      }),
    [values, binCount, domainMin, domainMax],
  );
  const slotW = plotW / Math.max(1, bins.length);
  const gap = Math.min(2, slotW * 0.2);
  const maxCount = Math.max(1, ...bins.map(bin => bin.count));
  const xAt = (seconds: number) =>
    PAD_X + ((clamp(seconds, domainMin, domainMax) - domainMin) / domainSpan) * plotW;
  const secondsAt = (x: number) => domainMin + clamp((x - PAD_X) / plotW, 0, 1) * domainSpan;
  const yAt = (count: number) => PLOT_TOP + plotHeight - (count / maxCount) * plotHeight;
  const yTicks = [...new Set([0, Math.ceil(maxCount / 2), maxCount])];
  const tickStep =
    NICE_STEPS.find(step => (step / domainSpan) * plotW >= MIN_TICK_GAP_PX) ??
    NICE_STEPS[NICE_STEPS.length - 1];
  const firstTick = Math.ceil(domainMin / tickStep) * tickStep;
  const xTicks = Array.from(
    { length: Math.floor((domainMax - firstTick) / tickStep) + 1 },
    (_, i) => firstTick + i * tickStep,
  );
  const selectable = Boolean(onSelectionChange);
  const shown: DurationRange | null = drag
    ? drag.moved
      ? {
          min: Math.min(drag.anchor, drag.current),
          max: Math.max(drag.anchor, drag.current),
        }
      : { min: drag.anchor, max: drag.anchor }
    : (selection ?? null);
  const inShown = (bin: { min: number; max: number }) =>
    shown === null || (bin.max > shown.min && bin.min < shown.max);
  const countIn = (range: DurationRange) =>
    values.filter(value => value >= range.min && value <= range.max).length;

  // Zooming keeps the duration under the pointer where it was on screen.
  const zoomAt = useCallback(
    (factor: number, clientX: number) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const next = clamp(zoom * factor, 1, MAX_ZOOM);
      if (next === zoom) return;
      const offsetX = clientX - scroller.getBoundingClientRect().left;
      pendingScroll.current = {
        fraction: (scroller.scrollLeft + offsetX - PAD_X) / plotW,
        offsetX,
      };
      setZoom(next);
    },
    [plotW, zoom],
  );
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const pending = pendingScroll.current;
    if (!scroller || !pending) return;
    pendingScroll.current = null;
    scroller.scrollLeft = PAD_X + pending.fraction * plotW - pending.offsetX;
    setScrollLeft(scroller.scrollLeft);
  }, [plotW]);

  // A committed filter starts a new view. Reset after release, never during the drag.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset on filter/domain changes, including clearing an open range whose domain stays the same.
  useLayoutEffect(() => {
    setZoom(1);
    setHover(null);
    setScrollLeft(0);
    pendingScroll.current = null;
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [domainMin, domainMax, selection?.min, selection?.max]);

  // A wheel with ⌘ or Ctrl held (a trackpad pinch arrives as one) zooms the chart rather than the
  // page; React's own wheel handler is passive and cannot stop the browser from zooming too.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      zoomAtRef.current(Math.exp(-event.deltaY * 0.01), event.clientX);
    };
    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, []);

  const pointerSeconds = (event: React.PointerEvent<SVGSVGElement>) =>
    secondsAt(event.clientX - event.currentTarget.getBoundingClientRect().left);
  const binAt = (seconds: number) =>
    bins.length > 0
      ? Math.min(bins.length - 1, Math.floor(((seconds - domainMin) / domainSpan) * bins.length))
      : null;

  const selectBin = (index: number) => {
    const bin = bins[index];
    if (!bin || bin.count === 0) return;
    const range = { min: bin.min, max: bin.max };
    if (onSelectionChange) onSelectionChange(range);
    else setZoomRange(range);
  };
  const targetBin = (target: EventTarget) => {
    const index = (target as Element).getAttribute("data-bin-index");
    return index === null ? null : Number(index);
  };

  const finishDrag = () => {
    const drag = dragRef.current;
    if (!drag) return;
    pointerCommitted.current = true;
    if (!drag.moved) {
      if (drag.bin !== null) selectBin(drag.bin);
      else onSelectionChange?.(null);
    } else {
      const range = {
        min: Math.min(drag.anchor, drag.current),
        max: Math.max(drag.anchor, drag.current),
      };
      if (!onSelectionChange) {
        setZoomRange(range);
      } else {
        // Dragged to the far end: the range stays open, so it keeps the slowest value in.
        onSelectionChange(
          range.max >= domainMax && (!selection || !Number.isFinite(selection.max))
            ? { ...range, max: Number.POSITIVE_INFINITY }
            : range,
        );
      }
    }
    setDrag(null);
  };

  const hoverBin = hover !== null && !drag ? bins[hover] : null;
  const edge = (seconds: number) => (Number.isFinite(seconds) ? seconds : domainMax);

  return (
    <div className="group/histogram relative" ref={containerRef}>
      <div
        className={
          title === false
            ? "absolute right-0 top-0 z-10 flex items-center gap-1.5"
            : "mb-1 flex min-h-7 flex-wrap items-center justify-between gap-x-3 gap-y-1"
        }
      >
        {title !== false && (
          <div className="flex min-w-0 items-center gap-1.5 text-xs text-text-dim">
            {title ?? (
              <span className="text-xs font-medium uppercase tracking-wide text-text-dim">
                {t("duration_distribution")}
              </span>
            )}
            <HelpHint
              standalone
              label={t("processPanel.histogramHelp")}
              text={[
                description,
                selectable
                  ? t("processPanel.histogramHintSelect")
                  : t("processPanel.histogramHintZoom"),
              ]
                .filter(Boolean)
                .join(" ")}
            />
          </div>
        )}
        <div className="flex shrink-0 items-center gap-1.5">
          {(zoom > 1 || zoomRange) && (
            <button
              type="button"
              onClick={() => {
                setZoom(1);
                setZoomRange(null);
              }}
              aria-label={t("processPanel.histogramResetZoom")}
              title={t("processPanel.histogramResetZoom")}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-outline bg-surface px-2 text-xs tabular-nums text-text-mid transition hover:bg-surface-strong hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              <ZoomOut className="h-3.5 w-3.5" aria-hidden="true" />
              {zoomRange
                ? t("processPanel.histogramResetZoom")
                : `${zoom.toFixed(zoom < 10 ? 1 : 0)}×`}
            </button>
          )}
          {onAskVariability && (
            <button
              type="button"
              onClick={onAskVariability}
              aria-label={t("ask_why_duration_variability")}
              className="pointer-events-none inline-flex h-7 items-center gap-1 rounded-md bg-surface-strong px-2 text-xs font-medium text-text opacity-0 shadow-sm transition group-hover/histogram:pointer-events-auto group-hover/histogram:opacity-100 focus:pointer-events-auto focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            >
              {t("ask_why")}
              <MessageCircleQuestion className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div className="relative flex">
        <svg width={AXIS_W} height={height} className="shrink-0" aria-hidden="true">
          {yTicks.map(count => (
            <text
              key={count}
              x={AXIS_W - 6}
              y={yAt(count) + 3}
              textAnchor="end"
              fill="var(--color-text-dim)"
              fontSize={10}
            >
              {count}
            </text>
          ))}
        </svg>
        <div
          ref={scrollerRef}
          data-testid="duration-histogram-scroller"
          className="min-w-0 flex-1 overflow-x-auto"
          onScroll={event => setScrollLeft(event.currentTarget.scrollLeft)}
        >
          <svg
            width={plotW + PAD_X * 2}
            height={height}
            role="group"
            aria-label={`${t("duration_distribution")}: ${countLabel} by ${t("duration")}`}
            tabIndex={-1}
            className="cursor-crosshair touch-none select-none outline-none focus-visible:ring-2 focus-visible:ring-info/30"
            onClick={event => {
              // A pointer release already committed the click or drag. Assistive activation
              // and synthetic clicks reach here directly and use the target bar's range.
              if (!pointerCommitted.current) {
                const index = targetBin(event.target);
                if (index !== null) selectBin(index);
              }
              pointerCommitted.current = false;
            }}
            onPointerDown={event => {
              if (event.button !== 0) return;
              event.preventDefault();
              event.currentTarget.focus({ preventScroll: true });
              event.currentTarget.setPointerCapture?.(event.pointerId);
              const seconds = pointerSeconds(event);
              pointerCommitted.current = false;
              setDrag({
                anchor: seconds,
                current: seconds,
                moved: false,
                bin: targetBin(event.target),
              });
            }}
            onKeyDown={event => {
              if (event.key === "Escape") {
                event.stopPropagation();
                if (dragRef.current) setDrag(null);
                else {
                  if (selection) onSelectionChange?.(null);
                  setZoomRange(null);
                  setZoom(1);
                }
              }
            }}
            onDoubleClick={() => {
              if (!selectable) {
                setZoomRange(null);
                setZoom(1);
              }
            }}
            onLostPointerCapture={() => setDrag(null)}
            onPointerMove={event => {
              const seconds = pointerSeconds(event);
              setHover(binAt(seconds));
              const drag = dragRef.current;
              if (!drag) return;
              const moved = drag.moved || Math.abs(xAt(seconds) - xAt(drag.anchor)) >= MIN_DRAG_PX;
              setDrag({ ...drag, current: seconds, moved });
            }}
            onPointerUp={finishDrag}
            onPointerCancel={() => setDrag(null)}
            onPointerLeave={() => setHover(null)}
          >
            {yTicks.map(count => (
              <line
                key={count}
                x1={PAD_X}
                x2={PAD_X + plotW}
                y1={yAt(count)}
                y2={yAt(count)}
                stroke="var(--color-outline)"
                opacity={count === 0 ? 0.9 : 0.5}
              />
            ))}
            {bins.map((bin, index) => {
              if (bin.count === 0) return null;
              const height = Math.max(3, (bin.count / maxCount) * plotHeight);
              const outlier = outlierAbove != null && bin.min >= outlierAbove;
              return (
                <rect
                  key={`${bin.min}-${bin.max}`}
                  data-testid={`histogram-bar-${index}`}
                  data-bin-index={index}
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer outline-none focus-visible:stroke-text focus-visible:stroke-2"
                  onKeyDown={event => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    selectBin(index);
                  }}
                  x={PAD_X + index * slotW + gap / 2}
                  y={PLOT_TOP + plotHeight - height}
                  width={Math.max(1, slotW - gap)}
                  height={height}
                  rx={Math.min(2, slotW / 4)}
                  fill={outlier ? "var(--color-danger)" : "var(--color-chart-blue)"}
                  opacity={
                    !inShown(bin)
                      ? MARK_OPACITY * 0.3
                      : hover === index
                        ? MARK_OPACITY_HOVER
                        : MARK_OPACITY
                  }
                  aria-label={`${formatDuration(bin.min)} – ${formatDuration(bin.max)}: ${bin.count} ${countLabel.toLowerCase()}`}
                />
              );
            })}
            {outlierAbove != null && outlierAbove > domainMin && outlierAbove < domainMax && (
              <g pointerEvents="none">
                <line
                  data-testid="histogram-outlier-threshold"
                  x1={xAt(outlierAbove)}
                  x2={xAt(outlierAbove)}
                  y1={PLOT_TOP - 6}
                  y2={PLOT_TOP + plotHeight}
                  stroke="var(--color-danger)"
                  strokeDasharray="3 3"
                  opacity={0.8}
                />
                <text
                  x={xAt(outlierAbove) + (xAt(outlierAbove) > plotW / 2 ? -4 : 4)}
                  y={PLOT_TOP - 6}
                  textAnchor={xAt(outlierAbove) > plotW / 2 ? "end" : "start"}
                  fill="var(--color-danger-strong)"
                  fontSize={10}
                >
                  {t("processPanel.histogramOutlierThreshold", {
                    threshold: formatDuration(outlierAbove),
                  })}
                </text>
              </g>
            )}
            {drag && shown && (
              <g data-testid="histogram-selection" pointerEvents="none">
                <rect
                  x={xAt(shown.min)}
                  y={PLOT_TOP}
                  width={Math.max(0, xAt(edge(shown.max)) - xAt(shown.min))}
                  height={plotHeight}
                  fill="var(--color-primary)"
                  opacity={0.08}
                />
                {[shown.min, edge(shown.max)].map((seconds, index) => (
                  <g key={index === 0 ? "from" : "to"}>
                    <line
                      x1={xAt(seconds)}
                      x2={xAt(seconds)}
                      y1={PLOT_TOP - 4}
                      y2={PLOT_TOP + plotHeight}
                      stroke="var(--color-text)"
                      strokeWidth={1.5}
                    />
                    <text
                      x={xAt(seconds)}
                      y={PLOT_TOP - 7}
                      textAnchor={index === 0 ? "start" : "end"}
                      dx={index === 0 ? 3 : -3}
                      fill="var(--color-text)"
                      fontSize={10}
                      fontWeight={600}
                    >
                      {formatAxisDuration(seconds, tickStep)}
                    </text>
                  </g>
                ))}
              </g>
            )}
            {xTicks.map(seconds => (
              <g key={seconds}>
                <line
                  x1={xAt(seconds)}
                  x2={xAt(seconds)}
                  y1={PLOT_TOP + plotHeight}
                  y2={PLOT_TOP + plotHeight + 4}
                  stroke="var(--color-text-dim)"
                  opacity={0.65}
                />
                <text
                  x={xAt(seconds)}
                  y={PLOT_TOP + plotHeight + 16}
                  textAnchor={
                    seconds === firstTick ? "start" : seconds === domainMax ? "end" : "middle"
                  }
                  fill="var(--color-text-dim)"
                  fontSize={10}
                >
                  {formatAxisDuration(seconds, tickStep)}
                </text>
              </g>
            ))}
          </svg>
        </div>
        {hoverBin && hoverBin.count > 0 && hover !== null && (
          <ChartTooltip
            x={AXIS_W + PAD_X + (hover + 0.5) * slotW - scrollLeft}
            y={yAt(hoverBin.count)}
            width={containerW}
            label={`${hoverBin.count} ${countLabel.toLowerCase()} (${formatAxisDuration(hoverBin.min, domainSpan / bins.length)}–${formatAxisDuration(hoverBin.max, domainSpan / bins.length)})`}
          />
        )}
        {drag?.moved && shown && (
          <ChartTooltip
            x={AXIS_W + (xAt(shown.min) + xAt(shown.max)) / 2 - scrollLeft}
            y={PLOT_TOP + plotHeight / 2}
            width={containerW}
            label={t("processPanel.histogramDragCount", {
              count: countIn(shown),
              label: countLabel.toLowerCase(),
            })}
          />
        )}
      </div>
      {selectable && selection && showSelectionSummary && (
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-dim">
          <span className="tabular-nums">
            {Number.isFinite(selection.max)
              ? t("processPanel.histogramSelectedRange", {
                  from: formatDuration(selection.min),
                  to: formatDuration(selection.max),
                })
              : t("processPanel.histogramSelectedMinimum", {
                  from: formatDuration(selection.min),
                })}
          </span>
          <button
            type="button"
            onClick={() => onSelectionChange?.(null)}
            className="rounded text-info hover:underline focus-visible:ring-2 focus-visible:ring-info/30"
          >
            {t("processPanel.histogramClearRange")}
          </button>
        </div>
      )}
    </div>
  );
};

export default DurationHistogram;
